import React, { useEffect, useRef, useState } from 'react';
import { connect } from "react-redux";

import { withT } from "services/translation/";

import { Row, Col, Button } from "react-bootstrap";

import { Link } from "react-router-dom";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./SiteNotes.scss";

import conxtdOut from "apis/conxtdOut";

import Icon from "components/Icons/Icon";
import Alert from "components/Common/Alert";

import { getEnterpriseFromRoute } from "services/redux/selectors/enterprises";
import { encodeNoteContent, decodeNoteContent } from "utils/noteContent";

import isEqual from 'lodash/isEqual'

const editorModules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ 'color': [] }, { 'background': [] }], 
    [{ font: [] }, { header: [1, 2, 3, 4, 5, 6, false] }],
    [{ list: "bullet" }, { list: "ordered" }, { indent: "-1" }, { indent: "+1" }, { align: [] }],
    // ["link", "image"],
    ["link"],
  ],
};

// const editorFormats = ["bold", "italic", "underline", "strike", "color", "background", "list", "indent", "align", "font", "header", "link", "image"];
const editorFormats = ["bold", "italic", "underline", "strike", "color", "background", "list", "indent", "align", "font", "header", "link"];

// Applies to the plain text of the note, not the underlying html markup
const MAX_NOTE_LENGTH = 3000;

const ACCEPTED_ATTACHMENT_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

const attachmentIconClassName = (type) => {
  if (type === "application/pdf") return "fas fa-file-pdf";
  if (type === "image/jpeg" || type === "image/png") return "fas fa-file-image";
  return "fas fa-file";
};

const GET_NOTES_URL = (enterpriseSiteId) => `/EnterpriseSites/getNote/${enterpriseSiteId}`;
const ADD_NOTES_URL = (enterpriseSiteId) => `/EnterpriseSites/addNote/${enterpriseSiteId}`;
// TODO: these don't exist on the backend yet - swap in the real endpoints once available
const GET_ATTACHMENTS_URL = (enterpriseSiteId) => `/single-sites/notes/${enterpriseSiteId}/attachments`;
const UPLOAD_ATTACHMENT_URL = (enterpriseSiteId) => `/single-sites/notes/${enterpriseSiteId}/attachments`;
const DELETE_ATTACHMENT_URL = (enterpriseSiteId, attachmentId) => `/single-sites/notes/${enterpriseSiteId}/attachments/${attachmentId}`;

function SiteNotes({
  t,
  site,
  onBackToOverview,
  setShowFooter,
  setFooterComponent,
}) {
  const [notes, setNotes] = useState("");
  const [noteLength, setNoteLength] = useState(0);
  const [savedContents, setSavedContents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef(null);
  const quillEditorRef = useRef(null);

  const enterpriseSiteId = site && site.id;

  useEffect(() => {
    let isMounted = true;

    const fetchNotes = async () => {
      setLoading(true);
      try {
        const response = await conxtdOut.get(GET_NOTES_URL(enterpriseSiteId));
        if (isMounted) {
          setNotes(decodeNoteContent(response.data.note) || "");
          setSavedContents(quillEditorRef.current.unprivilegedEditor.getContents());
          setNoteLength(quillEditorRef.current.unprivilegedEditor.getLength() - 1);
        }
      } catch (error) {
        console.log("Error fetching site notes", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const fetchAttachments = async () => {
      try {
        // TODO: uncomment once GET_ATTACHMENTS_URL is a real endpoint
        // const response = await conxtdOut.get(GET_ATTACHMENTS_URL(enterpriseSiteId));
        // if (isMounted) setAttachments(response.data.attachments || []);
      } catch (error) {
        console.log("Error fetching site note attachments", error);
      }
    };

    if (enterpriseSiteId) {
      fetchNotes();
      fetchAttachments();
    }

    return () => {
      isMounted = false;
    };
  }, [enterpriseSiteId]);

  // Quill's length always counts a trailing newline, so the visible text is one less
  const onNotesChange = (value, delta, source, editor) => {
    const length = editor.getLength() - 1;

    if (length > MAX_NOTE_LENGTH) {
      // Trim the overflow (e.g. from an oversized paste) rather than rejecting the whole change
      const quillEditor = quillEditorRef.current.getEditor();
      quillEditor.deleteText(MAX_NOTE_LENGTH, quillEditor.getLength());
      setNotes(quillEditor.root.innerHTML);
      setNoteLength(MAX_NOTE_LENGTH);
      return;
    }

    setNotes(value);
    setNoteLength(length);
  };

  const onSave = async () => {
    return new Promise(async (resolve, reject) => {
    setSaving(true);
    let errorOnSave = false;
    try {
      await conxtdOut.post(ADD_NOTES_URL(enterpriseSiteId), {
        note: encodeNoteContent(notes),
      });
      setSavedContents(quillEditorRef.current.unprivilegedEditor.getContents());

      Alert({
        text: t("save_success"),
        icon: "success",
        timer: 4000,
        timerProgressBar: true,
      });
    } catch (error) {
      console.log("Error saving site notes", error);
      errorOnSave = true;

      Alert({
        text: t("save_error"),
        icon: "error",
      });
    } finally {
      setSaving(false);
      if (errorOnSave) {
        reject();
      } else {
        resolve();
      }
    }
    })
  };

  // Kept up to date every render so the fixed footer button (registered below) always
  // calls the latest onSave, without needing to re-register the footer on every keystroke
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  useEffect(() => {
    setShowFooter(true);
    setFooterComponent(
      <div className="d-flex justify-content-center align-items-center h-100 px-4">
        <Button
          variant="success"
          disabled={loading || saving}
          onClick={() => onSaveRef.current()}
        >
          {saving ? t("saving_button") : t("save_button")}
        </Button>
      </div>
    );

    return () => {
      setShowFooter(false);
      setFooterComponent(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, saving]);

  const addFiles = (files) => {
    Array.from(files).forEach((file) => {
      if (!ACCEPTED_ATTACHMENT_TYPES.includes(file.type)) {
        Alert({ text: t("upload_error_type"), icon: "error" });
        return;
      }
      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        Alert({ text: t("upload_error_size"), icon: "error" });
        return;
      }

      const attachment = {
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        filename: file.name,
        type: file.type,
        url: URL.createObjectURL(file),
      };

      // TODO: uncomment once UPLOAD_ATTACHMENT_URL is a real endpoint, and reconcile
      // the optimistic entry above with whatever the backend returns (e.g. real id/url)
      // const formData = new FormData();
      // formData.append("file", file);
      // conxtdOut.post(UPLOAD_ATTACHMENT_URL(enterpriseSiteId), formData).catch((error) => {
      //   console.log("Error uploading site note attachment", error);
      // });

      setAttachments((prev) => [...prev, attachment]);
    });
  };

  const onFileInputChange = (e) => {
    if (e.target.files && e.target.files.length) addFiles(e.target.files);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  };

  const onViewAttachment = (attachment) => {
    window.open(attachment.url, "_blank", "noopener,noreferrer");
  };

  const onDeleteAttachment = async (attachment) => {
    const result = await Alert({
      title: t("delete_confirm_title"),
      text: t("delete_confirm_text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("delete_confirm_button"),
      cancelButtonText: t("cancel_button"),
    });

    if (!result.isConfirmed) return;

    // TODO: uncomment once DELETE_ATTACHMENT_URL is a real endpoint
    // conxtdOut.delete(DELETE_ATTACHMENT_URL(enterpriseSiteId, attachment.id)).catch((error) => {
    //   console.log("Error deleting site note attachment", error);
    // });

    setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));

    Alert({
      text: t("delete_success"),
      icon: "success",
      timer: 4000,
      timerProgressBar: true,
    });
  };

  const backToOverviewClick = () => {
    if (!isEqual(savedContents, quillEditorRef.current.unprivilegedEditor.getContents())) {
      Alert({
        text: "Unsaved changes",
        icon: "warning",
        confirmButtonText: "Save and close",
        confirmButtonColor: "#27ae60",
        showDenyButton: true,
        denyButtonText: "Close",
        denyButtonColor: "#e67e22",
        showCancelButton: true,
        cancelButtonText: "Cancel",
      }).then(async result => {
        if (result.value === false) {
          // deny button, but we're using it so say Close (without saving))
          onBackToOverview();
        } else if (result.value === true) {
          // Save and close
          try {
            await onSave();
            onBackToOverview();
          } catch (ignore) {};
        }
      });
    } else {
      onBackToOverview();
    }
  }

  return (
    <div style={{ paddingBottom: "64px" }}>
      <Row className="px-2 mt-2 mb-3">
        <div className="btn btn-default border-none side-pane-back text-dark">
          <Link to="#" onClick={backToOverviewClick}>
            <span className="back-to-overview"><Icon className="fas fa-chevron-left mr-2" /> {t("back_to_overview")}</span>
          </Link>
        </div>
      </Row>
      <Row>
        <Col>
          <h4 className="mt-0 pb-2 header-title">
            <Icon className="fas fa-note-sticky pr-1" />
            {t("title")}
          </h4>
        </Col>
      </Row>
      {/* <Row>
        <Col>
        <p>{t("edit_description")}</p>
        </Col>
      </Row> */}

      <Row className="px-2">
        <div className="col-12" id="quill-container">
          <label>{t("note_text_label")}</label>
          <ReactQuill
            ref={quillEditorRef}
            theme="snow"
            value={notes}
            onChange={onNotesChange}
            modules={editorModules}
            formats={editorFormats}
            readOnly={loading}
            bounds="#quill-container"
          />
          <small className={"d-block text-right mt-1 " + (noteLength >= MAX_NOTE_LENGTH ? "text-danger" : "text-muted")}>
            {t("character_count", noteLength, MAX_NOTE_LENGTH)}
          </small>
        </div>
      </Row>

      {/* <Row className="px-2 mt-4">
        <div className="col-12">
          <label><Icon className="fas fa-paperclip mr-2" />{t("attachments_title")}</label>

          <div
            className={"border rounded text-center p-4" + (dragActive ? " bg-light" : "")}
            style={{ borderStyle: "dashed" }}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
          >
            <Icon className="fas fa-cloud-upload-alt fa-2x text-muted mb-2" />
            <div>
              {t("dropzone_prompt")}{" "}
              <Link to="#" onClick={(e) => { e.preventDefault(); fileInputRef.current.click(); }}>
                {t("dropzone_browse")}
              </Link>
            </div>
            <small className="text-muted">{t("dropzone_hint")}</small>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={onFileInputChange}
              style={{ display: "none" }}
            />
          </div>
        </div>
      </Row>

      {attachments.length > 0 &&
        <Row className="px-2 mt-3">
          <div className="col-12">
            <label>{t("current_attachments_title")}</label>
            {attachments.map((attachment) => (
              <div key={attachment.id} className="d-flex align-items-center justify-content-between border-bottom py-2">
                <span>
                  <Icon className={attachmentIconClassName(attachment.type) + " mr-2"} />
                  {attachment.filename}
                </span>
                <span>
                  <Icon
                    className="fas fa-eye text-primary mr-3"
                    style={{ cursor: "pointer" }}
                    onClick={() => onViewAttachment(attachment)}
                  />
                  <Icon
                    className="fas fa-trash text-danger"
                    style={{ cursor: "pointer" }}
                    onClick={() => onDeleteAttachment(attachment)}
                  />
                </span>
              </div>
            ))}
          </div>
        </Row>
      } */}
    </div>
  )
}

const mapStateToProps = (state, props) => {
  return {
    enterprise: getEnterpriseFromRoute(state, props),
  };
};

export default withT(
  connect(mapStateToProps)(SiteNotes),
  "site_overview.side_pane.site_notes"
);